/**
 * Carries the faceted filters of the list a user creates from to whatever create surface opens next:
 * a modal over the grid, or a separate create screen.
 *
 * A session starts when the user picks an add action on a list, and is read by the create surface's
 * dropdowns as they mount. It lives above the router so it survives navigating into a create screen,
 * and is kept in a ref so starting one never re-renders the app.
 */

import React, { createContext, useCallback, useEffect, useMemo, useRef, type ReactNode } from 'react';
import type { ViewContextFilterValues } from '../createDefaultsFromViewContext';
import { CREATE_SURFACE_KIND, EMPTY_FILTER_VALUES, MODAL_OVER_LIST_SESSION_TTL_MS } from './constants';
import type { CreateSession, ListViewCreateSessionContextValue } from './types';
import { getCreateSurfaceKind } from './utils';

export const ListViewCreateSessionContext = createContext<ListViewCreateSessionContextValue>({
    startCreateSession: () => undefined,
    readCreateDefaultsFilterValues: () => EMPTY_FILTER_VALUES
});

export function ListViewCreateSessionProvider({ children }: { children: ReactNode }) {
    const sessionRef = useRef<CreateSession | undefined>(undefined);
    const expiryRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

    const clearExpiry = useCallback(() => {
        if (expiryRef.current) {
            clearTimeout(expiryRef.current);
            expiryRef.current = undefined;
        }
    }, []);

    useEffect(() => clearExpiry, [clearExpiry]);

    const startCreateSession = useCallback(
        (session: CreateSession) => {
            clearExpiry();
            sessionRef.current = session;
        },
        [clearExpiry]
    );

    const readCreateDefaultsFilterValues = useCallback((): ViewContextFilterValues => {
        const session = sessionRef.current;
        if (!session) {
            return EMPTY_FILTER_VALUES;
        }

        const surface = getCreateSurfaceKind(session, window.location.pathname);
        if (surface === CREATE_SURFACE_KIND.none) {
            clearExpiry();
            sessionRef.current = undefined;
            return EMPTY_FILTER_VALUES;
        }

        if (surface === CREATE_SURFACE_KIND.modalOverList) {
            // Nothing in the route says a create is open here. Keep the session with a TTL so async
            // field mounts can still inherit defaults, and refresh while reads continue.
            clearExpiry();
            expiryRef.current = setTimeout(() => {
                sessionRef.current = undefined;
                expiryRef.current = undefined;
            }, MODAL_OVER_LIST_SESSION_TTL_MS);
        }

        return session.filterValues;
    }, [clearExpiry]);

    const value = useMemo(
        () => ({ startCreateSession, readCreateDefaultsFilterValues }),
        [startCreateSession, readCreateDefaultsFilterValues]
    );

    return <ListViewCreateSessionContext.Provider value={value}>{children}</ListViewCreateSessionContext.Provider>;
}
